import { useMemo } from "react"
import { ExploreWidgetConfig } from "../../../config"
import { useExplorer } from "../../../providers"
import { useWidget } from "../../../../../providers/widget"
import {
  FLEX_ALT_HEADINGS,
  enumerateAlts,
  todayIso,
} from "../flexDates"

/**
 * The alternative-dates strip under the search bar (#870 slice 6).
 *
 * Toolkit `.hrtk-sbp-flex-alts` (templates/search-bar.php:1200-1208, rendered
 * by `renderAlts`, searchbar-pro.js:1197-1236). It sits below the bar rather
 * than inside the picker on purpose: it persists over the results, so a
 * visitor who searched "1 week in August" can jump to a different week without
 * reopening anything.
 *
 * Unlike the copy inside the picker — which stages into that dialog's draft —
 * clicking here APPLIES immediately. There is no dialog open to stage into, and
 * the reference refines live from this strip for the same reason.
 *
 * Renders only for an APPLIED flexible search; an exact-date search has no
 * alternatives to suggest.
 */
export default function FlexAltDates() {
  const { options } = useWidget<ExploreWidgetConfig>()
  const { state, setFilter } = useExplorer()

  const flexCfg = options.searchbar.flexDates
  const applied = state.flexDates
  // A stay length the admin has since switched off leaves the strip silent
  // rather than offering chips the picker itself no longer lists.
  const live = flexCfg.enabled && applied && flexCfg.stayLengths.includes(applied.stay) ? applied : null

  const todayStr = todayIso()
  const alts = useMemo(
    () => (live ? enumerateAlts(live.stay, live.month, todayStr) : []),
    [live, todayStr],
  )

  if (!live || !alts.length) return null

  const { checkin, checkout } = state.visitorFilters

  return (
    <div className="hfse-sbp-flex-alts">
      <span className="hfse-sbp-flex-alts-heading">{FLEX_ALT_HEADINGS[live.stay]}</span>
      <div className="hfse-sbp-flex-alts-track">
        {alts.map((alt) => {
          const active = alt.checkin === checkin && alt.checkout === checkout
          return (
            <button
              key={`${alt.checkin}-${alt.checkout}`}
              type="button"
              className={`hfse-sbp-flex-alts-chip${active ? ' hfse-sbp-flex-alts-chip-active' : ''}`}
              aria-current={active || undefined}
              onClick={() => setFilter({ checkin: alt.checkin, checkout: alt.checkout })}
            >
              {alt.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
